'use client'

import { Container } from '@/components/container'
import { SectionHeader } from '@/components/section-header'
import { MotionDiv } from '@/components/motion-wrapper'
import { useEffect, useRef } from 'react'
import { trackEvent } from '@/lib/utils'
import Image from 'next/image'

const testimonials = [
  {
    quote: 'Saisha Consultancy transformed our MVP from concept to launch in just 10 weeks. Their technical expertise and attention to detail exceeded our expectations.',
    author: 'Sarah Chen',
    role: 'Founder & CEO',
    company: 'Tech Startup',
    image: '/assets/placeholder-user.jpg',
  },
  {
    quote: 'The code quality and architecture they delivered set us up for scale. Their team augmentation services helped us ship features faster than we could have alone.',
    author: 'Michael Rodriguez',
    role: 'CTO',
    company: 'SaaS Company',
    image: '/assets/placeholder-user.jpg',
  },
  {
    quote: 'Their technical audit identified critical performance bottlenecks we didn\'t know existed. The optimization work improved our app speed by 3x.',
    author: 'Emily Watson',
    role: 'Product Manager',
    company: 'Enterprise Client',
    image: '/assets/placeholder-user.jpg',
  },
]

export function TestimonialsSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const hasTrackedView = useRef(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedView.current) {
            hasTrackedView.current = true
            trackEvent('section_viewed', {
              event_category: 'engagement',
              event_label: 'testimonials_section',
              value: 1,
            })
          }
        })
      },
      { threshold: 0.5 }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current)
      }
    }
  }, [])

  return (
    <section ref={sectionRef} id="testimonials" className="py-24 bg-white">
      <Container>
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <SectionHeader
            subheading="Testimonials"
            heading="What Our Clients Say"
            description="Don't just take our word for it. Here's what our clients have to say about working with us."
          />
        </MotionDiv>

        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent my-12" />

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <MotionDiv
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="relative rounded-xl border border-gray-200 bg-white p-8 shadow-sm hover:shadow-lg transition-shadow h-full flex flex-col">
                <div className="mb-6">
                  <svg
                    className="h-8 w-8 text-gray-300"
                    fill="currentColor"
                    viewBox="0 0 32 32"
                  >
                    <path d="M10 8c-3.3 0-6 2.7-6 6v10h10V14H8c0-1.1.9-2 2-2V8zm16 0c-3.3 0-6 2.7-6 6v10h10V14h-6c0-1.1.9-2 2-2V8z" />
                  </svg>
                </div>
                <p className="text-gray-700 mb-6 flex-grow italic">
                  &ldquo;{testimonial.quote}&rdquo;
                </p>
                <div className="flex items-center">

                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.author}</p>
                    <p className="text-sm text-gray-600">
                      {testimonial.role}, {testimonial.company}
                    </p>
                  </div>
                </div>
              </div>
            </MotionDiv>
          ))}
        </div>
      </Container>
    </section>
  )
}

